import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';

export interface HelpTextSegment {
  text: string;
  url?: string;
}

export interface HelpTextBlock {
  type: 'paragraph' | 'list';
  segments?: HelpTextSegment[];
  items?: HelpTextSegment[][];
}

@Component({
  selector: 'app-help-text-links',
  templateUrl: './help-text-links.component.html',
  styleUrl: './help-text-links.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class HelpTextLinksComponent implements OnChanges {
  @Input() text: string = '';

  public segments: HelpTextSegment[] = [];
  public blocks: HelpTextBlock[] = [];

  ngOnChanges(): void {
    const text = this.text || '';
    this.segments = this.buildSegments(text);
    this.blocks = this.buildBlocks(text);
  }

  private buildBlocks(text: string): HelpTextBlock[] {
    const blocks: HelpTextBlock[] = [];
    const lines = text.replace(/\r\n?/g, '\n').split('\n');
    let listItems: HelpTextSegment[][] = [];

    const flushList = () => {
      if (listItems.length > 0) {
        blocks.push({ type: 'list', items: listItems });
        listItems = [];
      }
    };

    for (const rawLine of lines) {
      const line = rawLine.trim();
      if (!line) {
        flushList();
        continue;
      }

      const lineBullet = line.match(/^[-*•·]\s+(.+)$/);
      if (lineBullet) {
        listItems.push(this.buildSegments(lineBullet[1].trim()));
        continue;
      }

      flushList();
      const inlineBulletParts = line.split(/\s+[•·]\s+/);
      if (inlineBulletParts.length > 1) {
        const introduction = inlineBulletParts.shift()?.trim();
        if (introduction) {
          blocks.push({ type: 'paragraph', segments: this.buildSegments(introduction) });
        }
        const items = inlineBulletParts
          .map(item => item.trim())
          .filter(item => item.length > 0)
          .map(item => this.buildSegments(item));
        if (items.length > 0) {
          blocks.push({ type: 'list', items });
        }
        continue;
      }

      blocks.push({ type: 'paragraph', segments: this.buildSegments(line) });
    }

    flushList();
    return blocks;
  }

  private buildSegments(text: string): HelpTextSegment[] {
    const segments: HelpTextSegment[] = [];
    const urlPattern = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/gi;
    let textStart = 0;
    let match: RegExpExecArray | null;

    while ((match = urlPattern.exec(text)) !== null) {
      if (match.index > textStart) {
        segments.push({ text: text.substring(textStart, match.index) });
      }

      const matchedValue = match[0];
      const url = matchedValue.replace(/[.,;:!?\])}]+$/, '');
      const trailingText = matchedValue.substring(url.length);
      segments.push({
        text: '(click here)',
        url: url.toLowerCase().startsWith('www.') ? `https://${url}` : url
      });
      if (trailingText) {
        segments.push({ text: trailingText });
      }

      textStart = match.index + matchedValue.length;
    }

    if (textStart < text.length) {
      segments.push({ text: text.substring(textStart) });
    }

    return segments;
  }
}
